import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon as TrendingUpIcon,
  ArrowTrendingDownIcon as TrendingDownIcon,
  ClockIcon,
  UsersIcon,
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { backendAPI } from '../../utils/api';

interface ReportFilters {
  period: 'last_7_days' | 'last_30_days' | 'last_90_days' | 'last_year' | 'custom';
  start_date?: string;
  end_date?: string;
  report_type: 'overview' | 'financial' | 'operational' | 'projects' | 'clients';
  compare_previous: boolean;
}



const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

export default function ReportsPage() {
  const [filters, setFilters] = useState<ReportFilters>({
    period: 'last_30_days',
    report_type: 'overview',
    compare_previous: false
  });



  // Executive Dashboard Data
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['executive-dashboard', filters.period],
    queryFn: () => backendAPI.getExecutiveDashboard(
      filters.period === 'last_7_days' ? '7' :
      filters.period === 'last_30_days' ? '30' :
      filters.period === 'last_90_days' ? '90' :
      filters.period === 'last_year' ? '365' : '30'
    )
  });



  // Project Profitability Data
  const { data: projectProfitability, isLoading: projectLoading } = useQuery({
    queryKey: ['project-profitability', filters.period],
    queryFn: () => {
      const endDate = new Date();
      const startDate = new Date();
      const days = filters.period === 'last_7_days' ? 7 :
                   filters.period === 'last_30_days' ? 30 :
                   filters.period === 'last_90_days' ? 90 :
                   filters.period === 'last_year' ? 365 : 30;
      startDate.setDate(endDate.getDate() - days);
      
      return backendAPI.getProjectProfitabilityReport({
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      });
    }
  });

  // AR Aging Data
  const { data: arAgingData, isLoading: arLoading } = useQuery({
    queryKey: ['ar-aging'],
    queryFn: () => backendAPI.getARAgingReport({})
  });

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };



  // Key Metrics Cards
  const keyMetrics = dashboardData ? [
    {
      title: 'Revenue',
      value: formatCurrency(dashboardData.financial_overview.revenue),
      change: '+12.5%',
      trend: 'up',
      icon: CurrencyDollarIcon,
      color: 'text-green-600'
    },
    {
      title: 'Profit Margin',
      value: formatPercentage(dashboardData.financial_overview.profit_margin_percentage),
      change: '+2.1%',
      trend: 'up',
      icon: TrendingUpIcon,
      color: 'text-blue-600'
    },
    {
      title: 'Utilization Rate',
      value: formatPercentage(dashboardData.operational_metrics.utilization_rate),
      change: '-1.3%',
      trend: 'down',
      icon: ClockIcon,
      color: 'text-orange-600'
    },
    {
      title: 'Active Projects',
      value: dashboardData.business_health.active_projects.toString(),
      change: '+3',
      trend: 'up',
      icon: DocumentChartBarIcon,
      color: 'text-purple-600'
    },
    {
      title: 'Outstanding AR',
      value: formatCurrency(dashboardData.cash_flow.outstanding_receivables),
      change: '-8.2%',
      trend: 'down',
      icon: CurrencyDollarIcon,
      color: 'text-red-600'
    },
    {
      title: 'Active Clients',
      value: dashboardData.business_health.active_clients.toString(),
      change: '+2',
      trend: 'up',
      icon: UsersIcon,
      color: 'text-indigo-600'
    }
  ] : [];

  // Generate sample trend data for charts
  const generateTrendData = (baseValue: number, periods: number = 12) => {
    const data = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < periods; i++) {
      const variance = (Math.random() - 0.5) * 0.2; // ±10% variance
      data.push({
        month: months[i],
        value: Math.round(baseValue * (1 + variance)),
        previous: Math.round(baseValue * 0.9 * (1 + variance))
      });
    }
    return data;
  };

  const revenueData = dashboardData ? generateTrendData(dashboardData.financial_overview.revenue / 12) : [];
  const profitData = dashboardData ? generateTrendData(dashboardData.financial_overview.profit / 12) : [];

  // AR Aging Chart Data
  const arAgingChartData = arAgingData ? [
    { name: 'Current (0-30)', value: arAgingData.aging_buckets.current.total, percentage: arAgingData.aging_buckets.current.percentage },
    { name: '31-60 Days', value: arAgingData.aging_buckets.days_31_60.total, percentage: arAgingData.aging_buckets.days_31_60.percentage },
    { name: '61-90 Days', value: arAgingData.aging_buckets.days_61_90.total, percentage: arAgingData.aging_buckets.days_61_90.percentage },
    { name: 'Over 90 Days', value: arAgingData.aging_buckets.over_90.total, percentage: arAgingData.aging_buckets.over_90.percentage }
  ] : [];

  // Top Projects by Profitability
  const topProjects = projectProfitability?.summary.top_performers?.slice(0, 5) || [];

  const isLoading = dashboardLoading || projectLoading || arLoading;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Advanced Analytics</h1>
            <p className="text-gray-600 mt-2">Comprehensive business intelligence and performance insights</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Period Filter */}
            <select
              value={filters.period}
              onChange={(e) => setFilters(prev => ({ ...prev, period: e.target.value as any }))}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="last_7_days">Last 7 Days</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="last_90_days">Last 90 Days</option>
              <option value="last_year">Last Year</option>
            </select>

            {/* Export Button */}
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
            {keyMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className={`p-2 rounded-lg ${
                      metric.color === 'text-green-600' ? 'bg-green-100' :
                      metric.color === 'text-blue-600' ? 'bg-blue-100' :
                      metric.color === 'text-orange-600' ? 'bg-orange-100' :
                      metric.color === 'text-purple-600' ? 'bg-purple-100' :
                      metric.color === 'text-red-600' ? 'bg-red-100' :
                      'bg-indigo-100'
                    }`}>
                      <Icon className={`h-6 w-6 ${metric.color}`} />
                    </div>
                    <div className={`flex items-center text-sm ${
                      metric.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {metric.trend === 'up' ? (
                        <TrendingUpIcon className="h-4 w-4 mr-1" />
                      ) : (
                        <TrendingDownIcon className="h-4 w-4 mr-1" />
                      )}
                      {metric.change}
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold text-gray-900">{metric.value}</h3>
                    <p className="text-sm text-gray-600">{metric.title}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Revenue Trend */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
                <button className="text-sm text-indigo-600 hover:text-indigo-700">
                  <EyeIcon className="h-4 w-4 inline mr-1" />
                  View Details
                </button>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value: any) => [formatCurrency(value), 'Revenue']} />
                  <Area type="monotone" dataKey="value" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="previous" stroke="#9CA3AF" fill="#9CA3AF" fillOpacity={0.1} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Profit Analysis */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Profit Analysis</h3>
                <button className="text-sm text-indigo-600 hover:text-indigo-700">
                  <EyeIcon className="h-4 w-4 inline mr-1" />
                  View Details
                </button>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={profitData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value: any) => [formatCurrency(value), 'Profit']} />
                  <Line type="monotone" dataKey="value" stroke="#10B981" strokeWidth={3} />
                  <Line type="monotone" dataKey="previous" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* AR Aging */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Accounts Receivable Aging</h3>
                <button className="text-sm text-indigo-600 hover:text-indigo-700">
                  <EyeIcon className="h-4 w-4 inline mr-1" />
                  View Details
                </button>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={arAgingChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {arAgingChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => [formatCurrency(value), 'Amount']} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Project Profitability */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Top Performing Projects</h3>
                <button className="text-sm text-indigo-600 hover:text-indigo-700">
                  <EyeIcon className="h-4 w-4 inline mr-1" />
                  View All
                </button>
              </div>
              <div className="space-y-4">
                {topProjects.map((project: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{project.project.name}</h4>
                      <p className="text-sm text-gray-600">{project.project.client?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(project.financial.gross_profit)}</p>
                      <p className={`text-sm ${
                        project.financial.gross_margin_percentage > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatPercentage(project.financial.gross_margin_percentage)} margin
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Business Intelligence Insights */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">AI-Powered Business Insights</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Revenue Forecast */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <TrendingUpIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <h4 className="font-medium text-blue-900">Revenue Forecast</h4>
                </div>
                <p className="text-sm text-blue-800">
                  Based on current trends, projected revenue for next month: <strong>{dashboardData ? formatCurrency(dashboardData.financial_overview.revenue * 1.12) : '$0'}</strong>
                </p>
              </div>

              {/* Efficiency Opportunity */}
              <div className="p-4 bg-orange-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <ClockIcon className="h-5 w-5 text-orange-600 mr-2" />
                  <h4 className="font-medium text-orange-900">Efficiency Opportunity</h4>
                </div>
                <p className="text-sm text-orange-800">
                  Increasing utilization rate by 5% could add <strong>$12,000</strong> monthly revenue.
                </p>
              </div>

              {/* Cash Flow Alert */}
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <CurrencyDollarIcon className="h-5 w-5 text-red-600 mr-2" />
                  <h4 className="font-medium text-red-900">Cash Flow Alert</h4>
                </div>
                <p className="text-sm text-red-800">
                  {arAgingData ? arAgingData.recommendations.urgent_collection : 0} invoices require immediate follow-up.
                </p>
              </div>
            </div>
          </div>

          {/* Performance Benchmarks */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Industry Benchmarks</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {dashboardData && [
                {
                  metric: 'Profit Margin',
                  current: dashboardData.financial_overview.profit_margin_percentage,
                  benchmark: 25,
                  unit: '%'
                },
                {
                  metric: 'Utilization Rate',
                  current: dashboardData.operational_metrics.utilization_rate,
                  benchmark: 80,
                  unit: '%'
                },
                {
                  metric: 'Avg Hourly Rate',
                  current: dashboardData.operational_metrics.average_hourly_rate,
                  benchmark: 150,
                  unit: '$'
                },
                {
                  metric: 'DSO',
                  current: dashboardData.cash_flow.days_sales_outstanding,
                  benchmark: 45,
                  unit: ' days'
                }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <h4 className="font-medium text-gray-900 mb-2">{item.metric}</h4>
                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${
                          item.current >= item.benchmark ? 'text-green-600 bg-green-200' : 'text-red-600 bg-red-200'
                        }`}>
                          {item.unit === '$' ? '$' : ''}{item.current.toFixed(1)}{item.unit !== '$' ? item.unit : ''}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold text-gray-600">
                          Target: {item.unit === '$' ? '$' : ''}{item.benchmark}{item.unit !== '$' ? item.unit : ''}
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
                      <div
                        style={{
                          width: `${Math.min((item.current / item.benchmark) * 100, 100)}%`
                        }}
                        className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${
                          item.current >= item.benchmark ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}